import type { FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'

export type SsoAccountLinkMode = 'loading' | 'expired' | 'choose' | 'link-email' | 'link-code'

export interface SsoAccountLinkPanelProps {
  mode: SsoAccountLinkMode
  organizationName?: string | null
  ssoEmail?: string
  email: string
  code: string
  error?: string
  submitting?: boolean
  onEmailChange: (value: string) => void
  onCodeChange: (value: string) => void
  onLinkExisting: () => void
  onCreateNew: () => void
  onSendOtp: (event: FormEvent) => void
  onConfirmLink: (event: FormEvent) => void
  onBackToChoose: () => void
  onBackToEmail: () => void
}

/**
 * Presentational SSO account-link panel shown when an unmatched SSO login
 * needs the user to link an existing OTP account or create a new one.
 */
export function SsoAccountLinkPanel({
  mode,
  organizationName,
  ssoEmail,
  email,
  code,
  error,
  submitting = false,
  onEmailChange,
  onCodeChange,
  onLinkExisting,
  onCreateNew,
  onSendOtp,
  onConfirmLink,
  onBackToChoose,
  onBackToEmail,
}: SsoAccountLinkPanelProps) {
  if (mode === 'loading') {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    )
  }

  if (mode === 'expired') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">{error || 'This linking session is no longer valid.'}</p>
        <p className="mt-2">Please sign in with your institution again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm text-slate-700">
        <p className="font-semibold text-slate-950">
          {organizationName || 'Your institution'} signed you in
        </p>
        {ssoEmail && <p className="mt-1">SSO email: {ssoEmail}</p>}
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
          {error}
        </div>
      )}

      {mode === 'choose' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Have you used Ludolio before?</p>
          <Button type="button" className="h-12 w-full rounded-xl" disabled={submitting} onClick={onLinkExisting}>
            Link existing account
          </Button>
          <Button type="button" variant="outline" className="h-12 w-full rounded-xl" disabled={submitting} onClick={onCreateNew}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create new account'}
          </Button>
        </div>
      )}

      {mode === 'link-email' && (
        <form className="space-y-4" onSubmit={onSendOtp}>
          <div className="space-y-2 text-left">
            <label htmlFor="sso-link-email" className="text-sm font-medium text-slate-900">
              Email you used before
            </label>
            <Input
              id="sso-link-email"
              type="email"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              className="h-12 rounded-xl"
              required
              autoFocus
            />
          </div>
          <Button type="submit" className="h-12 w-full rounded-xl" disabled={submitting || !email.trim()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send verification code'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" disabled={submitting} onClick={onBackToChoose}>
            Back
          </Button>
        </form>
      )}

      {mode === 'link-code' && (
        <form className="space-y-4" onSubmit={onConfirmLink}>
          <div className="space-y-2 text-left">
            <label htmlFor="sso-link-code" className="text-sm font-medium text-slate-900">
              Verification code
            </label>
            <Input
              id="sso-link-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
              className="h-12 rounded-xl tracking-[0.3em]"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500">Sent to {email}</p>
          </div>
          <Button type="submit" className="h-12 w-full rounded-xl" disabled={submitting || code.length < 6}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify and link'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" disabled={submitting} onClick={onBackToEmail}>
            Use a different email
          </Button>
        </form>
      )}
    </div>
  )
}
